// eslint-disable-next-line no-unused-vars
import React, { memo, useEffect, useState } from 'react';
import { func } from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';

import useFetch from 'Hooks/useFetch';
import useUser from 'Hooks/useUser';
import useNumberUtils from 'Hooks/useNumberUtils';
import useAsyncError from 'Hooks/useAsyncError';
import useAnalytics from 'Hooks/useAnalytics';
import IPTableComponent from 'Components/APIIntegrations/IPTable';
import { API_IP_ADDRESS, API_IP_ADDRESS_UPDATE_STATUS } from 'Constants';

const defaultValues = {
  ipAddress: '',
  isActive: true,
};

const IPTable = memo(({ setErrors }) => {
  const [IPAddressList, setIPAddressList] = useState([]);
  const [IPAddressListLoading, setIPAddressListLoading] = useState(true);
  const [isRequestPending, setIsRequestPending] = useState(false);
  const [activateLoading, setActivateLoading] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [edit, setEdit] = useState('');
  const [ipAddressToDelete, setIpAddressToDelete] = useState({});
  const fetcher = useFetch();
  const { formatDate } = useNumberUtils();
  const { t } = useTranslation();
  const throwError = useAsyncError();
  const { data: { email, loggedIn } = {} } = useUser();
  const analytics = useAnalytics();

  const {
    formState: { errors: rhfErrors },
    register,
    setValue,
    getValues,
    reset,
    watch,
    trigger,
  } = useForm({
    defaultValues,
    mode: 'onChange'
  });

  const ipAddressValue = watch('ipAddress');
  const isActiveValue = watch('isActive');
  const required = t('errors.fieldBlank');

  const validators = {
    ipAddress: {
      required,
      pattern: {
        value: /^((25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/,
        message: t('errors.invalidIPAddress'),
      },
    }
  };

  const handleModalOpen = id => {
    if (!id) {
      setAddModalOpen(true);
      return;
    }

    setEdit(id);

    const foundItem = IPAddressList.find(item => item.id === id) || {};
    const { ipAddress = '', isActive = false } = foundItem;

    reset({ ipAddress, isActive });
    setAddModalOpen(true);
  };

  const handleCloseModal = () => {
    setAddModalOpen(false);
    reset(defaultValues);
    setEdit('');
  };

  const handleDeleteModalOpen = id => {
    const foundItem = IPAddressList.find(item => item.id === id) || {};
    if (!foundItem) {
      return;
    }
    setIpAddressToDelete(foundItem);
    setDeleteModalOpen(true);
  };

  const handleDeleteModalClose = () => {
    setDeleteModalOpen(false);
    setIpAddressToDelete({});
  };

  const getIPAddressList = () => {
    setIPAddressListLoading(true);
    setIPAddressList([]);
    fetcher(API_IP_ADDRESS).then(res => {
      const parsedResponse = (res || []).map(item => ({
        ...item,
        createdAt: formatDate(item.createdAt),
        actions: {
          id: item.id,
          isActive: item.isActive,
        }
      }));
      setIPAddressList(parsedResponse);
    }).catch(err => throwError(err))
      .finally(() => setIPAddressListLoading(false));
  };

  const toggleIPAddress = (id, isActive) => {
    setActivateLoading(id);
    fetcher(`${API_IP_ADDRESS_UPDATE_STATUS}/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ isActive: !isActive }),
    }).then(() => {
      getIPAddressList();
    }).catch(err => setErrors([{ message: err.info.errors.map(i => i?.message).join('\r\n'), type: 'error' }]))
      .finally(() => setActivateLoading(false));
  };

  useEffect(() => {
    if (loggedIn) {
      getIPAddressList();
    }
  }, [email, loggedIn]);

  const handleDelete = async () => {
    analytics.track('delete_ip_address');
    setDeleteModalOpen(true);
    setIsRequestPending(true);

    fetcher(`${API_IP_ADDRESS}/${ipAddressToDelete.id}`, {
      method: 'DELETE',
    }).then(() => {
      getIPAddressList();
      setErrors([{ message: t('profile.apiInformationDeleteIp'), type: 'success' }]);
    }).catch(err => setErrors([{ message: err.info.errors.map(i => i?.message).join('\r\n'), type: 'error' }]))
      .finally(() => {
        setDeleteModalOpen(false);
        setIsRequestPending(false);
      });
  };

  const handleSubmit = async () => {
    analytics.track('submit_new_ip_address');
    await trigger();

    const rhfErrorsKeys = Object.keys(rhfErrors);
    if (rhfErrorsKeys.length || ipAddressValue === '') {
      // Stop execution if any errors
      return;
    }

    const path = edit ? `${API_IP_ADDRESS}/${edit}` : API_IP_ADDRESS;
    const method = edit ? 'PUT' : 'POST';

    setIsRequestPending(true);
    const data = getValues();
    fetcher(path, {
      method,
      body: JSON.stringify(data),
    }).then(() => {
      getIPAddressList();
      setErrors([{ message: t('profile.apiInformationSuccessIp'), type: 'success' }]);
    }).catch(err => {
      setErrors([{ message: err.info.errors.map(i => i?.message).join('\r\n'), type: 'error' }]);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }).finally(() => {
      handleCloseModal();
      setIsRequestPending(false);
    });
  };

  return (
    <IPTableComponent
      IPAddressList={ IPAddressList }
      IPAddressListLoading={ IPAddressListLoading }
      toggleIPAddress={ toggleIPAddress }
      activateLoading={ activateLoading }
      handleSubmit={ handleSubmit }
      isRequestPending={ isRequestPending }
      register={ register }
      setValue={ setValue }
      rhfErrors={ rhfErrors }
      validators={ validators }
      isActiveValue={ isActiveValue }
      addModalOpen={ addModalOpen }
      addModalClose={ handleCloseModal }
      handleModalOpen={ handleModalOpen }
      ipAddressValue={ ipAddressValue }
      deleteModalOpen={ deleteModalOpen }
      handleDeleteModalOpen={ handleDeleteModalOpen }
      deleteModalClose={ handleDeleteModalClose }
      handleDelete={ handleDelete }
      ipAddressToDelete={ ipAddressToDelete }
    />
  );
});

IPTable.propTypes = {
  setErrors: func,
};

IPTable.displayName = 'IPTableContainer';

export default IPTable;
